import type { LocalizedPathKey } from '@/utils/seo/localized-paths'

// Einstieg ueber die Lage des Kunden statt ueber unsere Leistungsliste. Wer
// "WooCommerce Agentur" sucht, weiss oft noch nicht, welche Leistung er
// braucht, wohl aber, was gerade nicht funktioniert. Jede Situation fuehrt auf
// die Landingpage, die genau diesen Fall behandelt.
//
// Nur Aussagen, die auf der jeweiligen Zielseite bereits stehen. Keine
// Zahlen, keine Zeitversprechen.
// Nur statische Routen: dynamische wie /leistungen/[slug] braeuchten Parameter.
type StaticPath = Exclude<LocalizedPathKey, `${string}[${string}]${string}`>

export type Situation = {
  id: string
  href: StaticPath
  kicker: { de: string; en: string }
  title: { de: string; en: string }
  text: { de: string; en: string }
  linkLabel: { de: string; en: string }
}

export const situations: Situation[] = [
  {
    id: 'uebernahme',
    href: '/lp/woocommerce-betreuung',
    kicker: { de: 'Shop ohne Betreuung', en: 'Shop without support' },
    title: { de: 'Der Shop läuft. Aber niemand kümmert sich.', en: 'The shop runs. But nobody looks after it.' },
    text: {
      de: 'Updates bleiben liegen, die bisherige Agentur reagiert kaum noch oder der Freelancer ist weg. Wir übernehmen bestehende WooCommerce-Shops ohne Neubau und beginnen mit einem kostenlosen WooCommerce-Check.',
      en: 'Updates pile up, the previous agency barely responds or the freelancer is gone. We take over existing WooCommerce shops without a rebuild, starting with a free WooCommerce check.'
    },
    linkLabel: { de: 'Zur Shop-Übernahme', en: 'Shop takeover' }
  },
  {
    id: 'migration',
    href: '/lp/woocommerce-migration',
    kicker: { de: 'Plattformwechsel', en: 'Platform switch' },
    title: { de: 'Shopware, Magento oder Shopify passt nicht mehr.', en: 'Shopware, Magento or Shopify no longer fits.' },
    text: {
      de: 'Das System ist veraltet, die laufenden Kosten steigen oder Sie wollen die volle Kontrolle über Ihren Code. Wir migrieren Ihren Shop zu WooCommerce, mit 301-Weiterleitungen und ohne Datenverlust.',
      en: 'The system is outdated, running costs keep rising or you want full control over your code. We migrate your shop to WooCommerce, with 301 redirects and without data loss.'
    },
    linkLabel: { de: 'Zur Migration', en: 'Migration' }
  },
  {
    id: 'b2b',
    href: '/lp/b2b-e-commerce',
    kicker: { de: 'B2B-Vertrieb', en: 'B2B sales' },
    title: { de: 'Ihre Geschäftskunden brauchen eigene Preise und Abläufe.', en: 'Your business customers need their own prices and processes.' },
    text: {
      de: 'Kundenspezifische Preise, Bestellprozesse für Firmenkunden und die Anbindung an Ihr ERP. Wir bilden Ihren B2B-Vertrieb im Shop ab, abgestimmt auf Ihre Prozesse.',
      en: 'Customer-specific prices, ordering processes for business customers and integration with your ERP. We map your B2B sales in the shop, aligned with your processes.'
    },
    linkLabel: { de: 'Zu B2B E-Commerce', en: 'B2B e-commerce' }
  },
  {
    id: 'neuer-shop',
    href: '/lp/woocommerce-agentur',
    kicker: { de: 'Neuer Shop', en: 'New shop' },
    title: { de: 'Ein neuer Shop, der Ihnen gehört.', en: 'A new shop that belongs to you.' },
    text: {
      de: 'Ohne Lizenzgebühren für die Plattform und ohne Bindung an einen Anbieter. Wir setzen Ihren WooCommerce-Shop von Konzept bis Betreuung um, rechtssicher mit Germanized oder German Market.',
      en: 'No platform licence fees and no vendor lock-in. We build your WooCommerce shop from concept to ongoing care, legally compliant for Germany with Germanized or German Market.'
    },
    linkLabel: { de: 'Zur WooCommerce-Entwicklung', en: 'WooCommerce development' }
  },
  {
    id: 'ki',
    href: '/ki/ki-automatisierung-woocommerce',
    kicker: { de: 'KI im Shop', en: 'AI in the shop' },
    title: { de: 'Wiederkehrende Arbeit kostet Sie zu viel Zeit.', en: 'Repetitive work is eating your time.' },
    text: {
      de: 'Produkttexte, Kundenanfragen, Retouren: Vieles davon lässt sich mit KI automatisieren, direkt in Ihrem bestehenden WooCommerce-Shop über REST-API und eigene Plugins.',
      en: 'Product copy, customer enquiries, returns: much of it can be automated with AI, directly in your existing WooCommerce shop via the REST API and custom plugins.'
    },
    linkLabel: { de: 'Zur KI-Automatisierung', en: 'AI automation' }
  }
]
